import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Salle de sport"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F17] via-[#0B0F17]/80 to-[#0B0F17]/30" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <Logo className="w-11 h-11" iconClass="w-6 h-6" />
            <div className="font-display font-bold text-2xl tracking-wider text-white">LAC ROSE FITNESS</div>
          </div>
          <div className="animate-fade-up">
            <h1 className="font-display text-5xl xl:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              PILOTEZ VOTRE<br />
              <span className="text-emerald-600 dark:text-emerald-400">SALLE DE SPORT</span><br />
              EN TOUTE PRÉCISION
            </h1>
            <p className="mt-4 text-slate-200 max-w-md text-base">
              Séances, équipements, nutrition et caisse journalière — tout votre club dans un seul centre de commande.
            </p>
          </div>
          <p className="text-xs text-slate-400" data-testid="login-branding">
            Ce logiciel est créé par IBM — © Copyright Ibn Bachir MultiMedia — Tél : 77 571 93 98
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Logo />
            <div className="font-display font-bold text-xl tracking-wider text-foreground">LAC ROSE FITNESS</div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-8 shadow-2xl">
            <h2 className="font-display text-2xl font-bold text-foreground tracking-tight">Connexion</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Accédez à votre espace de gestion</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  data-testid="login-email-input"
                  className="bg-background border-border text-foreground focus-visible:ring-emerald-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground/80">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="bg-background border-border text-foreground focus-visible:ring-emerald-500"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error-message">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
